/**
 * AI Tutor Bot - Message Formatting Utilities
 * Enhances AI messages with better formatting for educational content
 */

/**
 * Process the content of an AI message with formatting enhancements
 */
export const formatMessage = (contentDiv) => {
  if (!contentDiv) return;
  
  // Store original content
  const originalText = contentDiv.textContent;
  
  // Create new HTML content with formatting
  let formattedHTML = originalText;
  
  // Bold first: double asterisk for bold
  formattedHTML = formattedHTML.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
  // Then single asterisk for italic
  formattedHTML = formattedHTML.replace(/\*([^*]+?)\*/g, '<em>$1</em>');

  // Ensure spaces around inline bold/italic tags (handles "and**D**ivision" → "and <strong>D</strong>ivision")
  formattedHTML = formattedHTML.replace(/(\w)(<strong>)/g, '$1 $2');
  formattedHTML = formattedHTML.replace(/(<\/strong>)(\w)/g, '$1 $2');
  formattedHTML = formattedHTML.replace(/(\w)(<em>)/g, '$1 $2');
  formattedHTML = formattedHTML.replace(/(<\/em>)(\w)/g, '$1 $2');

  // LaTeX-style math expressions - replace common math operators and formatting
  formattedHTML = formatLatexMath(formattedHTML);
  
  // Handle proper math formulas with $ delimiters
  formattedHTML = formattedHTML.replace(/\$([^$]+)\$/g, '<span class="math-formula">$1</span>');
  
  // Improved spacing after punctuation
  formattedHTML = formattedHTML.replace(/(\.)(\w)/g, '$1 $2');
  formattedHTML = formattedHTML.replace(/(\?)(\w)/g, '$1 $2');
  formattedHTML = formattedHTML.replace(/(!)(\w)/g, '$1 $2');
  
  // Numbered lists - format them with proper spacing and styling
  formattedHTML = formattedHTML.replace(/(?:^|\n)(\d+)\.\s+(.*?)(?=\n\d+\.|\n\n|$)/g, 
    '<div class="step"><span class="step-number">$1.</span> $2</div>');
  
  // Create proper paragraphs for better readability
  formattedHTML = formattedHTML.replace(/\n\n/g, '</p><p>');
  
  // Promote lines ending with colon to subheadings
  formattedHTML = formattedHTML.replace(/<p>([^<]+?):<\/p>/g, '<h3>$1</h3>');
  
  // Handle bullet lists with better formatting
  formattedHTML = formattedHTML.replace(/(?:^|\n)•\s+(.*?)(?=\n•|\n\n|$)/g, 
    '<li>$1</li>');
  formattedHTML = formattedHTML.replace(/(<li>.*?<\/li>)+/g, '<ul class="bullet-list">$&</ul>');
  
  // Code blocks - properly format them
  formattedHTML = formattedHTML.replace(/```([^`]+)```/g, '<pre class="code-block">$1</pre>');
  
  // Inline code
  formattedHTML = formattedHTML.replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
  
  // Highlight important points
  formattedHTML = formattedHTML.replace(/(?:^|\n)IMPORTANT:(.+?)(?=\n|$)/g, 
    '<div class="important-note">⚠️ IMPORTANT: $1</div>');
  
  // Format educational terms with emojis (but use sparingly)
  formattedHTML = addEducationalEmojis(formattedHTML);
  
  // Check if there's a follow-up question at the end and format it specially
  const followUpMatch = originalText.match(/(?:^|\n)([A-Za-z0-9 ,?]+\?)\s*$/);
  if (followUpMatch) {
    // Replace the last question with a specially formatted version
    const question = followUpMatch[1];
    formattedHTML = formattedHTML.replace(question, 
      `</p><div class="follow-up-question"><strong>${question}</strong></div><p>`);
  }
  
  // Replace multiple spaces with a single space
  formattedHTML = formattedHTML.replace(/  +/g, ' ');
  
  // Wrap in paragraph if not already
  if (!formattedHTML.startsWith('<p>')) {
    formattedHTML = '<p>' + formattedHTML + '</p>';
  }
  
  // Create a new div with formatted content
  const tempDiv = document.createElement('div');
  tempDiv.className = 'formatted-content';
  tempDiv.innerHTML = formattedHTML;
  
  // Remove the questioning face emoji from the beginning of follow-up questions if it exists
  const followUpElements = tempDiv.querySelectorAll('.follow-up-question');
  followUpElements.forEach(element => {
    const text = element.innerHTML;
    if (text.includes('🤔')) {
      element.innerHTML = text.replace('🤔', '');
    }
  });
  
  // Replace content
  while (contentDiv.firstChild) {
    contentDiv.removeChild(contentDiv.firstChild);
  }
  
  contentDiv.appendChild(tempDiv);
};

/**
 * Format LaTeX-style math expressions
 */
const formatLatexMath = (text) => {
  let formatted = text;
  
  // Identify standalone math expressions (lines that look like LaTeX math)
  const mathLineRegex = /^([A-Za-z]+(?:\s+)?=(?:\s+)?.*\\?(?:times|cdot|div).*)/gm;
  formatted = formatted.replace(mathLineRegex, '<div class="math-expression">$1</div>');
  
  // Replace common LaTeX operators with proper symbols
  const operatorMap = {
    '\\times': '×',
    '\\cdot': '·',
    '\\div': '÷',
    '\\pm': '±',
    '\\leq': '≤',
    '\\geq': '≥',
    '\\neq': '≠',
    '\\approx': '≈',
    '\\pi': 'π',
    '\\sqrt': '√',
    '\\infty': '∞',
    '\\sum': 'Σ',
  };
  
  // Replace \text{ } with regular text
  formatted = formatted.replace(/\\text\{\s*([^}]*)\s*\}/g, '$1');
  
  // Replace operators
  Object.keys(operatorMap).forEach(operator => {
    const regex = new RegExp(operator.replace(/\\/g, '\\\\'), 'g');
    formatted = formatted.replace(regex, operatorMap[operator]);
  });
  
  // Replace fractions like \frac{a}{b} with a/b
  formatted = formatted.replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, '$1/$2');
  
  // Replace superscripts like x^{2} with x²
  formatted = formatted.replace(/\^2\b/g, '²');
  formatted = formatted.replace(/\^3\b/g, '³');
  formatted = formatted.replace(/\^([0-9])\b/g, '<sup>$1</sup>');
  formatted = formatted.replace(/\^\{([^{}]+)\}/g, '<sup>$1</sup>');
  
  // Replace subscripts like x_{i} with x₁
  formatted = formatted.replace(/_([0-9])\b/g, '<sub>$1</sub>');
  formatted = formatted.replace(/_\{([^{}]+)\}/g, '<sub>$1</sub>');
  
  return formatted;
};

/**
 * Add emojis to specific educational terms to make it more engaging for kids
 * (but use sparingly)
 */
const addEducationalEmojis = (text) => {
  // Count existing emojis to avoid adding too many (using Unicode property escape)
  const emojiCount = (text.match(/\p{Emoji}/gu) || []).length;
  
  // If there is already 1 or more emojis, don't add more
  if (emojiCount >= 1) return text;
  
  const emojiMap = {
    'multiplication': '✖️ multiplication',
    'division': '➗ division',
    'addition': '➕ addition',
    'subtraction': '➖ subtraction',
  };
  
  // Keep track of emojis added
  let emojisAdded = 0;
  const maxEmojis = 1 - emojiCount;
  
  // Replace words with emoji-enhanced versions, but limit the number
  for (const term in emojiMap) {
    if (emojisAdded >= maxEmojis) break;
    
    const regex = new RegExp(`\\b${term}\\b`, 'gi');
    const matches = text.match(regex);
    
    if (matches && matches.length > 0) {
      // Only replace the first occurrence
      text = text.replace(regex, emojiMap[term]);
      emojisAdded++;
    }
  }
  
  return text;
}; 